import type { ReactNode } from 'react';

import { ApiError } from '../api/client.js';

export function Loading({ label = '読み込み中' }: { label?: string }) {
  return (
    <div className="c-empty" role="status" aria-live="polite">
      <span className="c-spinner" aria-hidden="true" /> {label}
    </div>
  );
}

/**
 * 入力のどこが悪いのかを、フィールド名を日本語にして見せる。
 *
 * サーバーは「入力内容を確認してください」という文言と一緒に、
 * どのフィールドがなぜ弾かれたかを details で返している。
 * それを捨ててしまうと、利用者には直しようがない。
 */
const FIELD_LABELS: Readonly<Record<string, string>> = {
  slug: 'ID',
  title: 'タイトル',
  description: '説明',
  startTime: '開始時刻',
  endTime: '終了時刻',
  registrationStart: '参加登録の開始',
  registrationEnd: '参加登録の締切',
  freezeTime: '順位表の凍結時刻',
  rated: 'レート対象',
  rule: '配点規則',
  penaltyMinutes: 'ペナルティ (分)',
  theme: 'テーマ',
  difficulty: '難易度',
  languageSlug: '言語',
  testcaseCount: 'テストケース数',
  sourceCode: 'ソースコード',
  problemSlug: '問題 ID',
  username: 'ユーザー名',
  displayName: '表示名',
  email: 'メールアドレス',
  timeLimitMs: '実行時間制限',
  memoryLimitMb: 'メモリ制限',
};

function fieldLabel(path: string): string {
  // ネストしたパス (problems.0.label) は最後の要素で引く。
  const leaf = path.split('.').filter((p) => !/^\d+$/.test(p)).pop() ?? path;
  return FIELD_LABELS[leaf] ?? path;
}

export function ErrorNotice({ error }: { error: unknown }) {
  const message =
    error instanceof ApiError
      ? error.message
      : error instanceof Error
        ? error.message
        : '予期しないエラーが発生しました';
  const requestId = error instanceof ApiError ? error.requestId : undefined;
  const details = error instanceof ApiError ? error.details : undefined;
  const entries = Object.entries(details ?? {});

  return (
    <div className="c-alert c-alert--error" role="alert">
      <div>{message}</div>
      {entries.length > 0 && (
        <ul className="c-alert__details">
          {entries.map(([path, messages]) => (
            <li key={path}>
              {path === '_' ? messages.join(' / ') : `${fieldLabel(path)}: ${messages.join(' / ')}`}
            </li>
          ))}
        </ul>
      )}
      {requestId && (
        // 問い合わせ時に突き合わせるための ID。内部情報は含まない (SPEC 67)。
        <div className="u-text-sm u-mono u-mt-2">Request ID: {requestId}</div>
      )}
    </div>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="c-empty">{children}</div>;
}

export function PageHeader({
  title,
  actions,
  children,
}: {
  title: string;
  actions?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <header className="u-between u-mb-4">
      <div>
        <h1 className="u-mb-0">{title}</h1>
        {children}
      </div>
      {actions && <div className="u-row u-row--wrap">{actions}</div>}
    </header>
  );
}

export function Pagination({
  page,
  perPage,
  total,
  onChange,
}: {
  page: number;
  perPage: number;
  total: number;
  onChange: (page: number) => void;
}) {
  const lastPage = Math.max(1, Math.ceil(total / perPage));
  if (lastPage <= 1) {
    return null;
  }
  return (
    <nav className="c-pagination" aria-label="ページ送り">
      <button
        type="button"
        className="c-button c-button--sm"
        onClick={() => onChange(page - 1)}
        disabled={page <= 1}
      >
        前へ
      </button>
      <span aria-live="polite">
        {page} / {lastPage}
      </span>
      <button
        type="button"
        className="c-button c-button--sm"
        onClick={() => onChange(page + 1)}
        disabled={page >= lastPage}
      >
        次へ
      </button>
    </nav>
  );
}

/** ローカル時刻で表示する。DB は UTC なので変換はここだけで行う (SPEC 35)。 */
export function DateTime({ value }: { value: string }) {
  const date = new Date(value);
  return (
    <time dateTime={value} className="u-mono u-text-sm" title={date.toLocaleString('ja-JP')}>
      {date.toLocaleString('ja-JP', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })}
    </time>
  );
}

export function formatBytes(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined) return '—';
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

export function formatMs(ms: number | null | undefined): string {
  if (ms === null || ms === undefined) return '—';
  return `${ms} ms`;
}

/** 秒数を h:mm:ss へ。順位表のペナルティ表示に使う。 */
export function formatDuration(totalSeconds: number): string {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = Math.floor(totalSeconds % 60);
  const pad = (n: number): string => String(n).padStart(2, '0');
  return hours > 0 ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${minutes}:${pad(seconds)}`;
}

const DIFFICULTY_LABEL: Record<string, string> = {
  EASY: 'Easy',
  MEDIUM: 'Medium',
  HARD: 'Hard',
  EXPERT: 'Expert',
};

export function Difficulty({ value }: { value: string }) {
  return (
    <span className={`p-difficulty p-difficulty--${value.toLowerCase()}`}>
      {DIFFICULTY_LABEL[value] ?? value}
    </span>
  );
}
